import React, { useEffect, useRef, useState } from 'react';
import { TaskStatusButton } from './boards/TaskStatus';
import { taskStatusOf } from '../extensions/bundled/task-manager/taskStatus';
import TaskLabelChips from './TaskLabelChips';
import TaskCardMenu from './boards/TaskCardMenu';
import TaskCardMeta from './boards/TaskCardMeta';
import TaskAssigneeChip from './TaskAssigneeChip';
import { TaskKeyChip, useTaskKey } from './boards/TaskKeys';
import { cardMetaOf } from '../extensions/bundled/task-manager/taskCardMeta';
import './boards/TaskCard.css';

/**
 * A single Kanban card (Boards Redesign list 2, 12.10.2026 — artboards
 * "Kanban" + "Card anatomy"):
 *
 *   [status] Title text (click to rename)            [⋯ on hover/focus]
 *            label pills (wrap)
 *            priority · due · comments · description          [avatar]
 *
 * The labels and meta rows are indented under the title text and render only
 * when they have content (taskCardMeta.js), so a bare task is one 42 px row
 * (on a team board with the empty assign slot at the end of that row).
 * The title is inline-editable with a plain textarea (click to edit,
 * blur/Ctrl+Enter to save, Esc to cancel); the description lives in
 * TaskDetailModal — the ⋯ menu's Edit opens it.
 */
function TaskCard({
  task,
  dragHandleProps,
  isOverlay,
  onChange,
  onDelete,
  onOpenDetail,
  justCreatedRef,
  assignee = null,
  // Already resolved against the board's palette by the caller — see
  // TaskLabelChips on why a card never gets the board itself.
  labels = null,
  // Assigning from the card is a team-board affordance: `members` is empty and
  // `onAssign` absent on a personal board, which turns the avatar back into a
  // plain marker (and there is nothing to mark, since personal tasks are all
  // yours).
  members = [],
  currentUserId = null,
  onAssign = null,
  // Team boards only — a share resolves against a team's synced board, so on a
  // personal board there is nobody it could resolve for and the action is
  // absent rather than present-and-failing.
  onShare = null,
}) {
  const taskKey = useTaskKey(task.id);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title || '');
  const titleRef = useRef(null);

  // Cards created just now (via "Add Task") start in edit mode immediately,
  // no click needed. We key off an explicit "just created" set rather than
  // "title is empty" so that merely mounting a title-less card — e.g. when
  // switching back to this board's tab — does NOT steal focus into it.
  useEffect(() => {
    if (justCreatedRef?.current?.has(task.id)) {
      justCreatedRef.current.delete(task.id);
      setEditingTitle(true);
      setTitleDraft('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  useEffect(() => {
    if (editingTitle && titleRef.current) {
      const el = titleRef.current;
      el.focus();
      el.style.height = 'auto';
      el.style.height = `${Math.max(el.scrollHeight, 20)}px`;
      if (titleDraft) el.select();
    }
  }, [editingTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEditingTitle = () => {
    if (isOverlay) return;
    setTitleDraft(task.title || '');
    setEditingTitle(true);
  };

  const commitTitle = () => {
    setEditingTitle(false);
    const trimmed = titleDraft.trim();
    if (trimmed !== (task.title || '')) onChange({ title: trimmed || null });
  };

  const cancelTitleEdit = () => {
    setEditingTitle(false);
    setTitleDraft(task.title || '');
  };

  const [menuOpen, setMenuOpen] = useState(false);
  // Something to show in the avatar slot — an assignee, or the ability to set
  // one. On a personal board neither holds and the slot is not rendered.
  const assignable = !!onAssign || !!task.assigneeId;
  const meta = cardMetaOf(task, { assignable: !!onAssign });
  // Right-click opens the ⋯ menu at the pointer (owner, 13.10.2026).
  const [contextAt, setContextAt] = useState(null);
  const onContextMenu = (e) => {
    if (isOverlay || editingTitle) return;
    e.preventDefault();
    e.stopPropagation();
    setContextAt((prev) => ({ x: e.clientX, y: e.clientY, n: (prev?.n || 0) + 1 }));
  };

  return (
    <div
      className={`task-board-card${task.done ? ' task-board-card--done' : ''}${taskStatusOf(task) === 'dismissed' ? ' task-board-card--dismissed' : ''}${isOverlay ? ' task-board-card--overlay' : ''}${assignable ? ' task-board-card--assignable' : ''}${menuOpen ? ' task-board-card--active' : ''}${meta.placeholderOnly && !isOverlay ? ' task-board-card--assign-inline' : ''}`}
      {...(dragHandleProps || {})}
      onContextMenu={onContextMenu}
    >
      <div className="task-board-card-title-row">
        {/* Status leads the card, Linear / Trello style: the one action you
            take most often on a board, always in the same place. */}
        <TaskStatusButton className="task-board-card-status" task={task} onChange={onChange} />
        {editingTitle ? (
          <textarea
            ref={titleRef}
            className="task-board-card-title-input"
            rows={1}
            value={titleDraft}
            placeholder="Enter task title..."
            onChange={(e) => {
              setTitleDraft(e.target.value);
              const el = e.target;
              el.style.height = 'auto';
              el.style.height = `${Math.max(el.scrollHeight, 20)}px`;
            }}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelTitleEdit();
              } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { // ⌘ on a Mac too (card 50122def)
                e.preventDefault();
                commitTitle();
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div
            className={`task-board-card-title${task.title ? '' : ' task-board-card-title--empty'}`}
            onClick={(e) => {
              e.stopPropagation();
              startEditingTitle();
            }}
          >
            {task.title || 'Click to add title...'}
          </div>
        )}
        {meta.placeholderOnly && !isOverlay && (
          <span className="task-board-card-title-assign">
            <TaskAssigneeChip
              assigneeId={task.assigneeId}
              member={assignee}
              members={members}
              currentUserId={currentUserId}
              onAssign={onAssign}
              size={20}
            />
          </span>
        )}
        {!editingTitle && <TaskKeyChip taskKey={taskKey} className="task-board-card-key" />}
        {!isOverlay && !editingTitle && (
          <TaskCardMenu
            taskKey={taskKey}
            title={task.title || ''}
            priority={meta.priority}
            onOpenDetail={onOpenDetail}
            onShare={onShare}
            onDelete={onDelete}
            onPriority={(next) => onChange({ priority: next })}
            onOpenChange={setMenuOpen}
            contextAt={contextAt}
          />
        )}
      </div>

      {/* Labels qualify the title ("Fix login — bug, regression"); up to six
          pills, wrapping to a second line rather than clipping (card 2.3). */}
      <TaskLabelChips labels={labels} max={6} className="task-board-card-labels" />
      {meta.hasMeta && !meta.placeholderOnly && (
        <TaskCardMeta
          meta={meta}
          task={task}
          onChange={onChange}
          assignee={assignee}
          members={members}
          currentUserId={currentUserId}
          onAssign={onAssign}
          onOpenDetail={isOverlay ? null : onOpenDetail}
        />
      )}
    </div>
  );
}

// Memoised (owner, 13.10.2026: dragging on 80+ task boards) — SortableTaskCard
// hands it stable props, so a drag step re-renders only the cards that changed.
export default React.memo(TaskCard);
